<template>
  <div class="self-stretch flex flex-col px-3 py-2 bg-surface-rey-subtle border-t border-b border-border-rey-subtle"
       data-test="scheduled-thread-header">
    <div class="self-stretch flex items-center justify-between gap-2">
      <div class="flex-1 min-w-0 flex flex-col gap-0.5">
        <div class="font-sans text-sm font-bold text-text-default truncate" data-test="title">
          {{ $t('rey.conversations.scheduledThread') }}
        </div>
        <div v-if="active_label" class="font-sans text-xs text-text-subtle truncate" data-test="active-count">
          {{ active_label }}
        </div>
      </div>
    </div>
    <!-- Whatever the thread wants to say beneath the title — today, the folded earlier results. -->
    <div v-if="$slots.default" class="self-stretch mt-3" data-test="header-body">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * The Scheduled tasks thread's own header: what the thread is, and how many tasks are feeding it.
 *
 * Its own component rather than markup in MessengerWindow because it renders for exactly one thread,
 * and a block inside that template guarded on `active_thread_is_scheduled` would be a fourth thing
 * that file has to explain. Everything it shows is passed in; it fetches nothing and decides nothing.
 *
 * The active-task line is omitted rather than shown as "0 active" while the count is still unknown —
 * the tasks are fetched separately from the thread, so there is a moment where the honest answer is
 * "not yet", and a zero would read as "nothing is running".
 *
 * <b>No bulk mark-read control, though the spec lists one.</b> There are two different "unread" here
 * and neither wants a button:
 *   • The thread's own badge already clears itself — checkClearMessageUnreads runs as soon as the
 *     messages load, so by the time anyone could press it, it has done nothing.
 *   • The results inbox keeps its own per-run read state, and marking that needs (taskId, runId)
 *     pairs. A delivered result carries its task id but no RUN id, so the client cannot build the
 *     request from what is on screen.
 * Adding scheduledRunId to the delivery metadata would make the second one possible and is worth
 * doing; it is a server change, so it is raised rather than smuggled in here.
 */
export default {
  name: 'ScheduledThreadHeader',
  props: {
    /** Tasks currently enabled. Null while unknown — see the note on the component. */
    activeCount: {
      type: Number,
      required: false,
      default: null,
    },
    /** Tasks in total, enabled or paused. A paused task stays countable rather than disappearing. */
    totalCount: {
      type: Number,
      required: false,
      default: null,
    },
  },
  computed: {
    /**
     * "3 of 4 tasks active" — the spec's phrasing, which states the total deliberately so a paused
     * task remains visible in the count rather than vanishing from it.
     */
    active_label() {
      if (this.activeCount === null || this.totalCount === null) return '';

      return this.$t('messaging.scheduledHeader.activeTasks', {
        active: this.activeCount,
        total: this.totalCount,
      });
    },
  },
};
</script>
